import { Strings } from '@/lib/constants/strings'
import { Notation } from '@/lib/enums/bi-enums'
/**
 * Checks whether the provided value is a valid number.
 *
 * A value is considered valid if it is not `null`, not `undefined`, and not `NaN`.
 *
 * @param value - The value to validate as a number.
 * @returns `true` if the value is a valid number, otherwise `false`.
 */
export function isValidNumber(value: number | null | undefined): boolean {
	return value !== null && value !== undefined && !isNaN(value)
}
/**
 * Formats a number as a currency string with a specified number of significant digits,
 * via significant Digit and Notation.
 *
 * Notation
 *
 * This function adds $ at start of formated string.
 * @param value - The number to format.
 * @param significantDigit - The number of significant digits to include in the formatted string starting with the left most digit.
 * @param notation - The notation style to use, either "compact" or "standard". Compact means Number displayed as K, M, B, T as required. Standard means Number displayed with comma separated digits as '10,400'.
 * @returns The formatted currency string.
 */
export function formatCurrencyWithSignificantDigit(
	value: number | undefined,
	significantDigit: number,
	notation: 'compact' | 'standard'
): string {
	// added for typescript strict mode
	if (value === undefined) {
		return '-'
	}
	if (!isValidNumber(value)) {
		return '-'
	}
	return Intl.NumberFormat('en-US', {
		notation,
		currency: 'USD',
		style: 'currency',
		maximumSignificantDigits: significantDigit,
	}).format(value)
}

export function formatCurrencyWithFixedSignificantDigit(
	value: number | undefined,
	significantDigit: number,
	notation: 'compact' | 'standard'
): string {
	// added for typescript strict mode
	if (value === undefined) {
		return '-'
	}
	if (!isValidNumber(value)) {
		return '-'
	}
	if (Math.abs(value) < 1) {
		return '$' + value.toFixed(significantDigit - 1)
	}
	return Intl.NumberFormat('en-US', {
		notation,
		currency: 'USD',
		style: 'currency',
		minimumSignificantDigits: significantDigit,
		maximumSignificantDigits: significantDigit,
	}).format(value)
}

export function formatCurrency(
	value: number,
	notation: 'compact' | 'standard'
): string {
	return Intl.NumberFormat('en-US', {
		notation,
		currency: 'USD',
		style: 'currency',
		maximumFractionDigits: 0,
	}).format(value)
}

/// This function Specifies to format Percentage via significant Digit with compact notation
/// Value is a number which might comes from API or Text Field which needs to be format in desired format
/// Significant Digit Refers to the number,  a value of x significant digits means only x digits will be displayed starting with the left most digit.
/// This function add % at end of formated string.
// It must shows values in B, M,K,T format
export function formatPercentageWithSignificantDigit(
	value: number,
	significantDigit: number
): string {
	const format = Intl.NumberFormat('en-US', {
		notation: 'compact',
		style: 'decimal',
		maximumSignificantDigits: significantDigit,
	}).format(value)
	return format + '%'
}

export function formatPercentageWithFixedSignificantDigits(
	value: number,
	significantDigit: number
): string {
	if (!isValidNumber(value)) return '-'
	if (value < 1) return value.toFixed(significantDigit - 1) + '%';
	const format = Intl.NumberFormat('en-US', {
		notation: 'compact',
		style: 'decimal',
		minimumSignificantDigits: significantDigit,
		maximumSignificantDigits: significantDigit,
	}).format(value)
	return format + '%'
}

/// This function Specifies to format Number via significant Digit and Notation
/// Value is a number which might comes from API or Text Field which needs to be format in desired format
/// Significant Digit Refers to the number,  a value of x significant digits means only x digits will be displayed starting with the left most digit.
/// Notation Compact means Number to be displayed as K, M, B, T as required.
/// Notation Standard Represents Number to be displayed with comma separated digits as 10,400
/// This function shows value as required.
export function formatNumberWithSignificantDigit(
	value: number | null,
	significantDigit: number,
	notation: 'compact' | 'standard'
): string {
	if (!isValidNumber(value)) {
		return '-'
	}
	return Intl.NumberFormat('en-US', {
		notation,
		maximumSignificantDigits: significantDigit,
	}).format(value as number)
}

/**
 * Formats a number based on the specified rounding level and type. Format Currency, Percentage and Hash Number via Rounding Level and Notation
 *
 * @param value - The number to be formatted.
 * @param roundingLevel - The level of rounding to apply. If negative, it specifies the number of decimal places. Zero indicates to round to the nearest integer. Positive value x indicates to round to the nearest 10^x.
 * @param type - The type of formatting to apply. Can be "percentage", "currency", or "hash".
 * @returns The formatted number as a string. Currency shows $ at start. Percentage shows % at end. Hash needs no addition to format.
 */
export function formatNumberWithRoundingLevel(
	value: number,
	roundingLevel: number,
	type: 'percentage' | 'currency' | 'hash'
): string {
	let format

	const n = value
	if (roundingLevel < 0) {
		const isFloat = !Number.isInteger(value)
		format = value
		if (isFloat) {
			format = n.toFixed(Math.abs(roundingLevel))
		}
	} else {
		const signDigits = `${value}`.length - (roundingLevel - 1)
		format = Intl.NumberFormat('en-US', {
			style: 'decimal',
			maximumFractionDigits: 0,
			maximumSignificantDigits: signDigits > 0 ? signDigits : 1,
		}).format(Math.round(n))
	}

	if (type === 'percentage') {
		format += '%'
	} else if (type === 'currency') {
		format = '$' + format
	} else {
		format = `${format}`
	}
	return format.toString()
}

/**
 * Formats a number with standard sigfig parameterization if it is larger than the specified threshold.
 *
 * @param value - The number to be formatted.
 * @param threshold - The threshold value to compare against.  This is the lowest number to begin formatting.
 * @returns The formatted number as a string. Currency shows $ at start. Percentage shows % at end. Hash needs no addition to format.
 */
export function formatIfLarge(
	value: number | string | null | undefined,
	threshold: number
): string {
	if (value === null || value === undefined) return '-'

	// Sanitize the value to handle currency strings like "$0"
	const numericString =
		typeof value === 'string' ? value.replace(/[^0-9.-]+/g, '') : String(value)
	const numericValue = Number(numericString)

	if (!isValidNumber(numericValue)) return '-'

	return numericValue < threshold
		? new Intl.NumberFormat('en-US').format(numericValue)
		: formatNumberWithSignificantDigit(numericValue, 3, 'compact')
}

type CategoryMap<T extends string> = Record<T, string>

interface SeriesMapperTheme {
	palette: {
		dataVisual: {
			spectrum: CategoryMap<'red' | 'green'>
		}
		stroke: {
			neutral: {
				disabledDark: string
			}
		}
	}
}

/**
 * Returns an icon direction string based on the metric value.
 *
 * @param {number} metricValue - The metric value to evaluate.
 * @returns {'up' | 'down' | 'flat'} - Returns 'up' if the value is greater than 0,
 *   'down' if less than 0, and 'flat' if equal to 0.
 *
 * @example
 * getIcon(5);    // 'up'
 * getIcon(-3);   // 'down'
 * getIcon(0);    // 'flat'
 */
export function getIcon(
	metricValue: number | undefined
): 'up' | 'down' | 'flat' {
	if (!metricValue) return 'flat'
	if (metricValue > 0) return 'up'
	if (metricValue < 0) return 'down'
	return 'flat'
}

/**
 * Returns a color string based on the metric value and theme, with optional inverse logic.
 *
 * @param {number} metricValue - The metric value to evaluate.
 * @param {SeriesMapperTheme} theme - The theme object containing color mappings.
 * @param {boolean} [inverse=false] - If true, reverses the color mapping for positive/negative values.
 * @returns {string} - The color string from the theme based on the metric value and inverse flag.
 *
 * @example
 * getColor(5, theme) // returns theme.palette.dataVisual.spectrum.green
 * getColor(-3, theme) // returns theme.palette.dataVisual.spectrum.red
 * getColor(5, theme, true) // returns theme.palette.dataVisual.spectrum.red
 * getColor(0, theme) // returns theme.palette.stroke.neutral.disabledDark
 */
export function getColor(
	metricValue: number | undefined,
	theme: SeriesMapperTheme,
	inverse: boolean = false
): string {
	if (metricValue === undefined)
		return theme.palette.stroke.neutral.disabledDark
	if (inverse) {
		if (metricValue > 0) return theme.palette.dataVisual.spectrum.red
		if (metricValue < 0) return theme.palette.dataVisual.spectrum.green
	} else {
		if (metricValue > 0) return theme.palette.dataVisual.spectrum.green
		if (metricValue < 0) return theme.palette.dataVisual.spectrum.red
	}
	return theme.palette.stroke.neutral.disabledDark
}

const formatThreshold = 1000000
type FormatType = 'number' | 'currency' | 'commission'
// Helper function to format metric content
export function formatMetricContent(
	value: number | undefined,
	formatType: FormatType = 'number'
): string {
	if (value === undefined) return '-'
	switch (formatType) {
		case 'currency':
			return formatCurrency(value, Notation.standard)
		case 'commission':
			return Strings.COMMISSION_RATE(
				String(formatNumberWithSignificantDigit(value, 3, 'compact'))
			)
		case 'number':
		default:
			return String(formatIfLarge(value, formatThreshold))
	}
}

export function parseCurrencyToNumber(val?: string): number {
	return val ? Number(val.replace(/[^0-9.-]+/g, '')) : 0
}
